import { useCallback, useEffect, useMemo, useState } from "react";
import { isListingPlaceholderImage } from "../../../constants/automapListingConstants";
import {
  formatVehicleTitle,
  getVehicleListingInitials,
} from "../../../utils/automapListing/listingFormatters";

function resolveListingImageUrl(primaryImageUrl, listing) {
  const candidate = primaryImageUrl || listing?.primaryImageUrl || null;
  if (!candidate || isListingPlaceholderImage(candidate)) return null;
  return candidate;
}

/**
 * Buyer-safe listing image with initials fallback (Milestone 3 / M17 performance).
 */
export default function VehicleListingImage({
  listing,
  primaryImageUrl,
  className = "",
  loading = "lazy",
  fetchPriority,
  decoding = "async",
}) {
  const title = formatVehicleTitle(listing || {});
  const initials = useMemo(() => getVehicleListingInitials(listing || {}), [listing]);
  const resolvedSrc = useMemo(
    () => resolveListingImageUrl(primaryImageUrl, listing),
    [primaryImageUrl, listing]
  );
  const [src, setSrc] = useState(resolvedSrc);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(resolvedSrc);
    setFailed(false);
  }, [resolvedSrc, listing?.id]);

  const showInitials = !src || failed;
  const alt = showInitials
    ? `Imagen no disponible para ${title}`
    : `Foto de ${title}`;

  const handleError = useCallback(() => {
    if (failed) return;
    setFailed(true);
    setSrc(null);
  }, [failed]);

  if (showInitials) {
    return (
      <div
        className={`vehicle-listing-image__fallback ${className}`.trim()}
        role="img"
        aria-label={alt}
      >
        <span className="vehicle-listing-image__initials" aria-hidden="true">
          {initials}
        </span>
      </div>
    );
  }

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
      onError={handleError}
    />
  );
}
